import localforage from 'localforage'
import { create } from 'zustand'
import { fetchUserPreferences, saveUserPreference } from '@api/preferences'
import { readThemeCookie, saveThemeCookie } from '@helpers/themeCookie'
import type { ThemeMode, ThemeState } from '@app-types/stores/theme'

const themeStorageKey = 'spa:theme:mode'
const themePreferenceKey = 'themeMode'
const defaultThemeMode: ThemeMode = 'dark'

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

function saveThemeMode(mode: ThemeMode) {
  saveThemeCookie(mode)
  void localforage.setItem(themeStorageKey, mode)
}

async function loadStoredThemeMode() {
  const cookieMode = readThemeCookie()

  if (cookieMode) {
    return cookieMode
  }

  try {
    const storedMode = await localforage.getItem(themeStorageKey)

    return isThemeMode(storedMode) ? storedMode : defaultThemeMode
  } catch {
    return defaultThemeMode
  }
}

function saveBackendThemeMode(csrfToken: string | null, mode: ThemeMode) {
  saveThemeMode(mode)

  if (csrfToken === null) {
    return
  }

  void saveUserPreference(themePreferenceKey, mode, csrfToken)
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readThemeCookie() ?? defaultThemeMode,
  isLoaded: false,
  csrfToken: null,
  userId: null,

  loadThemeMode: async (userId = null, csrfToken = null) => {
    const state = get()

    if (state.isLoaded && state.userId === userId) {
      return
    }

    try {
      if (userId === null || csrfToken === null) {
        set({
          csrfToken: null,
          isLoaded: true,
          mode: await loadStoredThemeMode(),
          userId: null,
        })
        return
      }

      const preferences = await fetchUserPreferences()
      const preferenceMode = preferences[themePreferenceKey]
      const storedMode = await loadStoredThemeMode()
      const mode = isThemeMode(preferenceMode) ? preferenceMode : storedMode

      if (preferenceMode === undefined) {
        saveBackendThemeMode(csrfToken, mode)
      } else {
        saveThemeMode(mode)
      }

      set({
        csrfToken,
        isLoaded: true,
        mode,
        userId,
      })
    } catch {
      set({
        csrfToken,
        isLoaded: true,
        mode: await loadStoredThemeMode(),
        userId,
      })
    }
  },

  toggleThemeMode: () => {
    set((state) => {
      const mode = state.mode === 'dark' ? 'light' : 'dark'

      saveBackendThemeMode(state.csrfToken, mode)

      return { mode }
    })
  },
}))
